import axios from 'axios';

// Auto-detect backend URL based on current domain
const getBackendUrl = () => {
  // In production on custom domain, use same origin (Kubernetes routes /api to backend)
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    // Custom domain (localedgehq.com) or production deployments
    if (hostname === 'localedgehq.com' || hostname.endsWith('.emergent.host')) {
      return window.location.origin;
    }
  }
  // Fallback to env variable for preview/development
  return process.env.REACT_APP_BACKEND_URL || '';
};

const BACKEND_URL = getBackendUrl();
const API_URL = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth
export const register = (data) => api.post('/auth/register', data);
export const login = (data) => api.post('/auth/login', data);
export const getMe = () => api.get('/auth/me');

// Plans
export const getPlans = () => api.get('/plans');

// Checkout
export const createCheckout = (planId, originUrl) => 
  api.post('/checkout/create', { plan_id: planId, origin_url: originUrl });
export const getCheckoutStatus = (sessionId) => 
  api.get(`/checkout/status/${sessionId}`);

// Subscription
export const getMySubscription = () => api.get('/subscription/me');
export const cancelSubscription = () => api.post('/subscription/cancel');

// Business
export const createBusiness = (data) => api.post('/business', data);
export const getMyBusiness = () => api.get('/business/me');
export const updateMyBusiness = (data) => api.put('/business/me', data);
export const deleteMyBusiness = () => api.delete('/business/me');

// Public
export const getPublicBusinesses = (category, subcategory, search) => {
  const params = new URLSearchParams();
  if (category && category !== 'All') params.append('category', category);
  if (subcategory && subcategory !== 'All') params.append('subcategory', subcategory);
  if (search) params.append('search', search);
  return api.get(`/businesses?${params.toString()}`);
};
export const getBusinessById = (id) => api.get(`/businesses/${id}`);
export const getCategories = () => api.get('/categories');

// Admin
export const getSubscribers = () => api.get('/admin/subscribers');
export const getAdminBusinesses = () => api.get('/admin/businesses');
export const toggleBusinessPublish = (id) => api.put(`/admin/business/${id}/toggle-publish`);
export const toggleBusinessFounding = (id) => api.put(`/admin/business/${id}/toggle-founding`);
export const adminDeleteBusiness = (id) => api.delete(`/admin/business/${id}`);
export const getRevenue = () => api.get('/admin/revenue');
export const getAdminStats = () => api.get('/admin/stats');
export const getAdminAnalytics = () => api.get('/admin/analytics');

// Analytics
export const trackEvent = (businessId, eventType, referrerCategory = null, referrerSubcategory = null) => 
  api.post('/analytics/track', { 
    business_id: businessId, 
    event_type: eventType,
    referrer_category: referrerCategory,
    referrer_subcategory: referrerSubcategory
  });
export const getMyBusinessAnalytics = () => api.get('/analytics/my-business');
export const getBusinessAnalytics = (businessId) => api.get(`/analytics/business/${businessId}`);

// Chat Support
export const sendChatMessage = (message, sessionId = null) => 
  api.post('/chat', { message, session_id: sessionId });
export const escalateToSupport = (email, message, sessionId = null) => 
  api.post('/chat/escalate', { email, message, session_id: sessionId });

// Chat Analytics (Admin)
export const getChatAnalytics = () => api.get('/admin/chat-analytics');
export const getEscalations = (status = null) => {
  const params = status ? `?status=${status}` : '';
  return api.get(`/admin/escalations${params}`);
};
export const resolveEscalation = (escalationId) => 
  api.put(`/admin/escalations/${escalationId}/resolve`);

export default api;
